import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { StorefrontSection } from '@mlm/shared';
import { storefrontIcon } from '../../lib/icons';
import { bannerStyle } from '../../lib/banner-style';

type Cfg = Extract<StorefrontSection, { type: 'richLanding' }>['config'];

/** Internal path → router Link; `#anchor` / external → <a> (works with scrollNav). */
function CtaLink({
  href,
  className,
  children,
}: {
  href: string;
  className: string;
  children: ReactNode;
}) {
  if (href.startsWith('/'))
    return (
      <Link to={href} className={className}>
        {children}
      </Link>
    );
  return (
    <a href={href} className={className}>
      {children}
    </a>
  );
}

/**
 * One parameterized landing band. Generic + token-driven — all copy comes from
 * config, colors from theme tokens (so it reskins per tenant). Three looks:
 * corporateHero (full-bleed image+gradient), welcome (white intro + cards),
 * ctaBanner (gradient call-to-action).
 */
export function RichLandingSection({ section }: { section: StorefrontSection }) {
  const cfg = section.config as Cfg;

  if (cfg.variant === 'welcome') {
    return (
      <section className="bg-card py-14">
        <div className="mx-auto max-w-[900px] px-4 text-center">
          {cfg.eyebrow && (
            <div className="mb-2 text-xs font-bold uppercase tracking-[0.16em] text-primary">
              {cfg.eyebrow}
            </div>
          )}
          <h2 className="font-heading text-[32px] font-extrabold leading-tight text-foreground">
            {cfg.heading}
          </h2>
          {(cfg.bodyParas ?? (cfg.subheading ? [cfg.subheading] : [])).slice(0, 4).map((p, i) => (
            <p
              key={i}
              className="mx-auto mt-3 max-w-[760px] text-[15.5px] leading-relaxed text-muted-foreground"
            >
              {p}
            </p>
          ))}
          {cfg.featureCards && cfg.featureCards.length > 0 && (
            <div className="mt-9 grid gap-6 sm:grid-cols-3">
              {cfg.featureCards.slice(0, 3).map((c, i) => {
                const Icon = storefrontIcon(c.icon);
                return (
                  <div
                    key={i}
                    className="rounded-2xl bg-card p-6 shadow-card transition-transform hover:-translate-y-1"
                  >
                    <span className="mx-auto mb-4 flex h-[72px] w-[72px] items-center justify-center rounded-full border-2 border-primary text-primary">
                      <Icon className="h-8 w-8" />
                    </span>
                    <div className="text-[17px] font-bold text-foreground">{c.title}</div>
                    <p className="mt-1.5 text-[13.5px] leading-relaxed text-muted-foreground">
                      {c.body}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    );
  }

  if (cfg.variant === 'ctaBanner') {
    return (
      <section className="py-16 text-white" style={{ background: 'var(--brand-gradient)' }}>
        <div className="mx-auto max-w-[720px] px-4 text-center">
          <h2 className="font-heading text-[34px] font-extrabold leading-tight">{cfg.heading}</h2>
          {cfg.subheading && (
            <p className="mx-auto mt-3 max-w-[620px] text-[15.5px] opacity-95">{cfg.subheading}</p>
          )}
          <div className="mt-7 flex flex-wrap justify-center gap-4">
            {cfg.primaryCta && (
              <CtaLink
                href={cfg.primaryCta.href}
                className="rounded-[4px] bg-white px-6 py-3 text-sm font-extrabold text-primary transition-transform hover:-translate-y-0.5"
              >
                {cfg.primaryCta.label}
              </CtaLink>
            )}
            {cfg.secondaryCta && (
              <CtaLink
                href={cfg.secondaryCta.href}
                className="rounded-[4px] border border-white/80 px-6 py-3 text-sm font-extrabold text-white transition-colors hover:bg-white/10"
              >
                {cfg.secondaryCta.label}
              </CtaLink>
            )}
          </div>
        </div>
      </section>
    );
  }

  // corporateHero
  return (
    <section
      className="relative flex min-h-[460px] items-center text-white"
      style={bannerStyle({ imageKey: cfg.backgroundImageKey, gradient: 'var(--brand-gradient)' })}
    >
      {cfg.overlay !== false && cfg.backgroundImageKey && (
        <div
          className="absolute inset-0"
          style={{ background: 'var(--brand-gradient)', opacity: 0.72 }}
        />
      )}
      <div
        className={`relative mx-auto w-full max-w-[1180px] px-6 ${cfg.align === 'left' ? '' : 'text-center'}`}
      >
        <div className={`max-w-[720px] ${cfg.align === 'left' ? '' : 'mx-auto'}`}>
          {cfg.eyebrow && (
            <span className="inline-block rounded-full bg-white/15 px-3.5 py-1 text-xs font-bold uppercase tracking-[0.16em]">
              {cfg.eyebrow}
            </span>
          )}
          <h1 className="mt-4 font-heading text-[52px] font-extrabold leading-[1.02]">
            {cfg.heading}
          </h1>
          {cfg.subheading && (
            <p className="mt-4 text-[16.5px] leading-relaxed opacity-95">{cfg.subheading}</p>
          )}
          <div
            className={`mt-7 flex flex-wrap gap-4 ${cfg.align === 'left' ? '' : 'justify-center'}`}
          >
            {cfg.primaryCta && (
              <CtaLink
                href={cfg.primaryCta.href}
                className="rounded-[4px] bg-white px-6 py-3 text-sm font-extrabold text-primary transition-transform hover:-translate-y-0.5"
              >
                {cfg.primaryCta.label}
              </CtaLink>
            )}
            {cfg.secondaryCta && (
              <CtaLink
                href={cfg.secondaryCta.href}
                className="rounded-[4px] border border-white/80 px-6 py-3 text-sm font-extrabold text-white transition-colors hover:bg-white/10"
              >
                {cfg.secondaryCta.label}
              </CtaLink>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
